/**
 * The verified artist check.
 *
 * ## It makes one claim
 *
 * That this is a real artist and the account is genuinely theirs. Not
 * "official", not "notable", not "trusted" - a badge that stands for several
 * things at once is a badge nobody can read, including the people granting it.
 * The tooltip says the one thing so that nobody has to guess.
 *
 * ## Drawn the way every platform draws it
 *
 * A tick in a scalloped disc, filled in the accent. Somebody who has seen this
 * on another service should not have to work out what it means here, which is
 * the same argument `Explicit` makes for its grey square - and the reason not
 * to invent a nicer mark.
 *
 * ## Sized and spaced in `em`, not pixels
 *
 * It sits beside names at every size on this platform - a 13px row in a track
 * list and a 32px profile heading - and a fixed 14px mark is too big in one and
 * too small in the other. `em` makes it a proportion of whatever it stands next
 * to, and the gap scales with it, so the pair looks the same everywhere.
 *
 * The `vertical-align` is the part that is not obvious. An inline-flex box with
 * no text in it has its *bottom edge* on the baseline, so it hangs above the
 * word rather than beside it - which reads as an icon that is floating. Pulling
 * it down by a tenth of an em puts its middle against the middle of the
 * lowercase letters, which is where the eye expects it.
 *
 * ## Right of the name, never left
 *
 * `Explicit` goes on the left of a title because it qualifies the work before
 * you read it. This goes on the right of a name because it qualifies the person
 * after you have. Putting them on the same side would make two very different
 * statements look like one row of icons.
 *
 * ## Named for screen readers
 *
 * The glyph is out of the accessibility tree and the label carries the meaning,
 * so it is announced once, as words, after the name it belongs to.
 */
export function Verified({
  className = '',
  size = '0.85em',
}: {
  className?: string;
  /**
   * Any CSS length. Relative by default, so it tracks the text it sits beside.
   *
   * Pass a fixed size only where the mark has to line up with something other
   * than its own line - never to make it bigger for emphasis.
   */
  size?: string | number;
}) {
  return (
    <span
      role="img"
      aria-label="Verified artist"
      title="Verified artist"
      data-verified
      className={`inline-flex shrink-0 items-center text-accent ${className}`}
      style={{
        width: size,
        height: size,
        marginLeft: '0.35em',
        verticalAlign: '-0.1em',
      }}
    >
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="h-full w-full">
        <path d="M12 1.5l2.4 2 3.1-.3 1 3 2.7 1.6-1 3 1 3-2.7 1.6-1 3-3.1-.3-2.4 2-2.4-2-3.1.3-1-3L2.8 14.8l1-3-1-3 2.7-1.6 1-3 3.1.3z" />
        <path
          d="M7.8 12.2l2.6 2.6 5.2-5.6"
          fill="none"
          stroke="var(--color-accent-ink)"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}
